import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import styled from "styled-components";

import { MinusIcon, PlusIcon } from "../../icons/glyphs";
import { TooltipIconButton } from "../IconButton";
import { useZoomPanTransform } from "../ZoomPan";
import { controlTransition } from "../foundations/controlStyles";
import {
  AnnotationViewport,
  CapturedVideoSurface,
  EmptyRaster,
  Raster,
  ViewportOverlayActions,
} from "./AnnotationViewportPrimitives";
import {
  CapturedFrameAnnotationOverlay,
  type CoordinatePointPreview,
} from "./CapturedFrameAnnotationOverlay";
import { AnnotationRenderResourceImage } from "./AnnotationRenderResourceImage";
import type {
  AnnotationFrameDraft,
  AnnotationPointKind,
  AnnotationTarget,
  CapturedFrameRenderResource,
  FrameTargetAnnotationDraft,
} from "./types";

const TRANSFORM_META_IDLE_MS = 1500;

export type { CoordinatePointPreview } from "./CapturedFrameAnnotationOverlay";

const ViewportZoomControls = styled.div<{ $visible: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.1rem;
  padding: 0.1rem;
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: ${({ theme }) => theme.color.bg.surfaceOverlay};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? "0" : "-0.2rem")});
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  ${controlTransition("opacity", "transform")}

  &:focus-within {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
`;

const ViewportContextActions = styled.div`
  display: inline-flex;
  align-items: center;
  pointer-events: auto;
`;

const ZoomResetButton = styled(TooltipIconButton)`
  min-width: 2.8rem;
  font-variant-numeric: tabular-nums;
  pointer-events: auto;
`;

const TransformMeta = styled.div<{ $visible: boolean }>`
  position: absolute;
  right: 0.8rem;
  bottom: 0.8rem;
  padding: 0.38rem 0.5rem;
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: ${({ theme }) => theme.color.bg.surfaceOverlay};
  color: ${({ theme }) => theme.color.text.default};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-variant-numeric: tabular-nums;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: none;
  ${controlTransition("opacity")}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export function CapturedFrameViewport({
  frame,
  frameResource,
  targets,
  mediaSrc,
  poster,
  crossOrigin,
  label,
  coordinateGuideVisible,
  coordinatePreview,
  annotationsVisible = true,
  editable = true,
  interactionActive = true,
  contextActions,
  removePointLabel,
  resetViewLabel,
  zoomInLabel,
  zoomOutLabel,
  onPoint,
  onRemovePoint,
}: {
  frame: AnnotationFrameDraft;
  frameResource?: CapturedFrameRenderResource | null;
  targets: readonly { target: AnnotationTarget; draft?: FrameTargetAnnotationDraft }[];
  mediaSrc?: string;
  poster?: string | null;
  crossOrigin?: "anonymous" | "use-credentials";
  label: string;
  coordinateGuideVisible: boolean;
  coordinatePreview: CoordinatePointPreview | null;
  annotationsVisible?: boolean;
  editable?: boolean;
  interactionActive?: boolean;
  contextActions?: ReactNode;
  removePointLabel: string;
  resetViewLabel: string;
  zoomInLabel: string;
  zoomOutLabel: string;
  onPoint: (kind: AnnotationPointKind, x: number, y: number) => void;
  onRemovePoint: (targetId: string, pointId: string) => void;
}) {
  const idleCursor = editable && annotationsVisible ? "cell" : "default";
  const zoomPan = useZoomPanTransform({ idleCursor, locked: !interactionActive, minZoom: 1 });
  const { containerRef: setZoomPanContainer, consumeSuppressedClick } = zoomPan;
  const [controlsVisible, setControlsVisible] = useState(true);
  const [transformMetaVisible, setTransformMetaVisible] = useState(true);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const controlsTimerRef = useRef<number | null>(null);
  const transformMetaTimerRef = useRef<number | null>(null);
  const transformMetaKeyboardFocusRef = useRef(false);
  const transformMetaPointerFocusRef = useRef(false);
  const negativePointerIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (interactionActive) return;

    // A completed Ctrl+drag suppresses the browser's trailing click once. The
    // viewport remains mounted below transient tracking UI, so clear that
    // gesture-only state when the layer becomes inactive while retaining its
    // zoom/pan transform for a smooth return.
    consumeSuppressedClick();
    negativePointerIdRef.current = null;
  }, [consumeSuppressedClick, interactionActive]);

  const cancelControlsHide = useCallback(() => {
    if (controlsTimerRef.current === null) return;
    window.clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = null;
  }, []);

  const scheduleControlsHide = useCallback(() => {
    cancelControlsHide();
    controlsTimerRef.current = window.setTimeout(() => {
      controlsTimerRef.current = null;
      if (viewportRef.current?.contains(document.activeElement)) return;
      setControlsVisible(false);
    }, 1800);
  }, [cancelControlsHide]);

  const revealControls = useCallback(() => {
    setControlsVisible(true);
    scheduleControlsHide();
  }, [scheduleControlsHide]);

  const cancelTransformMetaHide = useCallback(() => {
    if (transformMetaTimerRef.current === null) return;
    window.clearTimeout(transformMetaTimerRef.current);
    transformMetaTimerRef.current = null;
  }, []);

  const scheduleTransformMetaHide = useCallback(() => {
    cancelTransformMetaHide();
    if (transformMetaKeyboardFocusRef.current) return;
    transformMetaTimerRef.current = window.setTimeout(() => {
      transformMetaTimerRef.current = null;
      setTransformMetaVisible(false);
    }, TRANSFORM_META_IDLE_MS);
  }, [cancelTransformMetaHide]);

  const setViewportRef = useCallback(
    (element: HTMLDivElement | null) => {
      viewportRef.current = element;
      setZoomPanContainer(element);
    },
    [setZoomPanContainer],
  );

  useEffect(() => {
    scheduleControlsHide();
    return cancelControlsHide;
  }, [cancelControlsHide, scheduleControlsHide]);

  useEffect(() => {
    setTransformMetaVisible(true);
    if (zoomPan.isPanning || transformMetaKeyboardFocusRef.current) {
      cancelTransformMetaHide();
    } else {
      scheduleTransformMetaHide();
    }
    return cancelTransformMetaHide;
  }, [
    cancelTransformMetaHide,
    frame.frameId,
    scheduleTransformMetaHide,
    zoomPan.isPanning,
    zoomPan.panX,
    zoomPan.panY,
    zoomPan.zoom,
  ]);

  const addPointFromEvent = (event: ReactMouseEvent<HTMLDivElement>, kind: AnnotationPointKind) => {
    if (zoomPan.consumeSuppressedClick()) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left - zoomPan.panX) / (rect.width * zoomPan.zoom);
    const y = (event.clientY - rect.top - zoomPan.panY) / (rect.height * zoomPan.zoom);
    onPoint(kind, x, y);
  };

  const handleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!editable || !annotationsVisible) return;
    if (event.ctrlKey) return;
    addPointFromEvent(event, "include");
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const negativePointArmed =
      event.button === 2 && negativePointerIdRef.current === event.pointerId;
    if (negativePointerIdRef.current === event.pointerId) negativePointerIdRef.current = null;
    if (!editable || !annotationsVisible || !negativePointArmed || event.ctrlKey) return;
    addPointFromEvent(event, "exclude");
  };

  const isTransformed = zoomPan.zoom !== 1 || zoomPan.panX !== 0 || zoomPan.panY !== 0;

  return (
    <AnnotationViewport
      ref={setViewportRef}
      role="img"
      tabIndex={0}
      aria-label={label}
      data-annotation-layer-visible={annotationsVisible ? "true" : "false"}
      onPointerEnter={revealControls}
      onPointerMove={revealControls}
      onPointerDown={(event) => {
        event.currentTarget.setAttribute("data-annotation-pointer-focus", "");
        revealControls();
        if (coordinateGuideVisible && event.ctrlKey && event.button === 0) {
          event.stopPropagation();
        }
        if (editable && annotationsVisible && event.button === 2 && !event.ctrlKey) {
          negativePointerIdRef.current = event.pointerId;
        }
        zoomPan.handlePointerDown(event);
      }}
      onPointerUp={handlePointerUp}
      onPointerCancel={(event) => {
        if (negativePointerIdRef.current === event.pointerId) negativePointerIdRef.current = null;
      }}
      onWheel={revealControls}
      onFocusCapture={revealControls}
      onBlurCapture={(event) => {
        if (event.target === event.currentTarget) {
          event.currentTarget.removeAttribute("data-annotation-pointer-focus");
        }
        if (!event.currentTarget.contains(event.relatedTarget)) scheduleControlsHide();
      }}
      onClick={handleClick}
      onContextMenu={(event) => event.preventDefault()}
      style={
        {
          "--c6-annotation-aspect-ratio": String(frame.width / frame.height),
          "--c6-annotation-cursor": zoomPan.interactionCursor,
        } as CSSProperties
      }
    >
      <Raster style={{ ...zoomPan.transformStyle, transition: "none" }}>
        {frameResource ? (
          <AnnotationRenderResourceImage image={frameResource.image} kind="captured-frame" />
        ) : mediaSrc ? (
          <CapturedVideoSurface
            src={mediaSrc}
            poster={poster}
            crossOrigin={crossOrigin}
            controls={false}
            showLabel={false}
            playing={false}
            currentTimeSeconds={frame.timestampSeconds}
            aria-label=""
          />
        ) : poster ? (
          <img src={poster} alt="" />
        ) : (
          <EmptyRaster />
        )}
        {annotationsVisible ? (
          <CapturedFrameAnnotationOverlay
            guideVisible={coordinateGuideVisible}
            preview={coordinatePreview}
            targets={targets}
            inverseZoom={1 / zoomPan.zoom}
            editable={editable}
            removePointLabel={removePointLabel}
            onRemovePoint={onRemovePoint}
          />
        ) : null}
      </Raster>
      <ViewportOverlayActions>
        {contextActions ? <ViewportContextActions>{contextActions}</ViewportContextActions> : null}
        <ViewportZoomControls
          $visible={controlsVisible}
          data-testid="viewport-zoom-controls"
          onPointerMove={revealControls}
          onPointerDownCapture={() => {
            transformMetaPointerFocusRef.current = true;
            transformMetaKeyboardFocusRef.current = false;
            scheduleTransformMetaHide();
          }}
          onPointerUpCapture={() => {
            transformMetaPointerFocusRef.current = false;
          }}
          onPointerCancelCapture={() => {
            transformMetaPointerFocusRef.current = false;
          }}
          onPointerLeave={() => {
            transformMetaPointerFocusRef.current = false;
          }}
          onFocusCapture={() => {
            revealControls();
            if (transformMetaPointerFocusRef.current) return;
            transformMetaKeyboardFocusRef.current = true;
            cancelTransformMetaHide();
            setTransformMetaVisible(true);
          }}
          onBlurCapture={(event) => {
            if (event.currentTarget.contains(event.relatedTarget)) return;
            transformMetaKeyboardFocusRef.current = false;
            scheduleTransformMetaHide();
          }}
        >
          <TooltipIconButton
            size="sm"
            variant="ghost"
            tooltip={{ content: zoomOutLabel, placement: "bottom" }}
            aria-label={zoomOutLabel}
            disabled={!zoomPan.canZoomOut}
            onPointerDown={(event) => {
              revealControls();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.stopPropagation();
              zoomPan.zoomOut();
            }}
          >
            <MinusIcon />
          </TooltipIconButton>
          <ZoomResetButton
            size="sm"
            variant="ghost"
            tooltip={{ content: resetViewLabel, placement: "bottom" }}
            aria-label={resetViewLabel}
            disabled={!isTransformed}
            onPointerDown={(event) => {
              revealControls();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.stopPropagation();
              zoomPan.reset();
            }}
          >
            {zoomPan.zoom.toFixed(1)}x
          </ZoomResetButton>
          <TooltipIconButton
            size="sm"
            variant="ghost"
            tooltip={{ content: zoomInLabel, placement: "bottom" }}
            aria-label={zoomInLabel}
            disabled={!zoomPan.canZoomIn}
            onPointerDown={(event) => {
              revealControls();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.stopPropagation();
              zoomPan.zoomIn();
            }}
          >
            <PlusIcon />
          </TooltipIconButton>
        </ViewportZoomControls>
      </ViewportOverlayActions>
      <TransformMeta $visible={transformMetaVisible} data-testid="viewport-transform-meta">
        Zoom {zoomPan.zoom.toFixed(1)}x · Pan {Math.round(zoomPan.panX)}, {Math.round(zoomPan.panY)}
      </TransformMeta>
    </AnnotationViewport>
  );
}
